import React from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SearchResults = ({ results = [], query = '', isLoading = false }) => {
  const navigate = useNavigate();

  const getArtistTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'music':
        return 'bg-purple-100 text-purple-800';
      case 'visual':
        return 'bg-blue-100 text-blue-800';
      case 'model':
        return 'bg-pink-100 text-pink-800';
      case 'actor':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewArtist = (artistId) => {
    navigate(`/artist-detail-editor-view?id=${artistId}`);
  };

  const handleQuickEdit = (artistId, section) => {
    navigate(`/artist-detail-editor-view?id=${artistId}&section=${section}`);
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 elevation-1">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-3">
            <div className="animate-spin">
              <Icon name="Loader2" size={20} className="text-accent" />
            </div>
            <span className="text-text-secondary">Searching artists...</span>
          </div>
        </div>
      </div>
    );
  }

  if (query && results?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 elevation-1">
        <div className="text-center py-8">
          <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No artists found</h3>
          <p className="text-text-secondary">
            No artists match your search for "{query}". Try different keywords.
          </p>
        </div>
      </div>
    );
  }

  if (!query) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg elevation-1">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-text-primary">
          Search Results ({results?.length})
        </h3>
        <p className="text-sm text-text-secondary">
          Found {results?.length} artist{results?.length !== 1 ? 's' : ''} for "{query}"
        </p>
      </div>
      <div className="divide-y divide-border">
        {results?.map((artist) => (
          <div key={artist?.id} className="p-4 hover:bg-muted transition-smooth">
            <div className="flex items-start space-x-4">
              {/* Artist Avatar */}
              <div className="flex-shrink-0">
                <Image
                  src={artist?.avatar}
                  alt={artist?.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              </div>
              
              {/* Artist Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="text-base font-medium text-text-primary truncate">
                    {artist?.name}
                  </h4>
                  <span className={`px-2 py-1 text-xs rounded-full ${getArtistTypeColor(artist?.type)}`}>
                    {artist?.type}
                  </span>
                  {artist?.isVerified && (
                    <Icon name="CheckCircle" size={16} className="text-accent" />
                  )}
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-text-secondary mb-2">
                  <span>@{artist?.handle}</span>
                  <span>•</span>
                  <span>{artist?.followers} followers</span>
                  <span>•</span>
                  <span>Last active {artist?.lastActive}</span>
                </div>
                
                {artist?.bio && (
                  <p className="text-sm text-text-secondary line-clamp-2 mb-3">
                    {artist?.bio}
                  </p>
                )}
                
                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Eye"
                    iconPosition="left"
                    onClick={() => handleViewArtist(artist?.id)}
                  >
                    View Profile
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    iconPosition="left"
                    onClick={() => handleQuickEdit(artist?.id, 'profile')}
                  >
                    Edit
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={() => handleQuickEdit(artist?.id, 'calendar')}
                  >
                    Calendar
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Image"
                    iconPosition="left"
                    onClick={() => handleQuickEdit(artist?.id, 'work')}
                  >
                    Works
                  </Button>
                </div>
              </div>
              
              {/* Status Indicator */}
              <div className="flex-shrink-0">
                <div className={`w-3 h-3 rounded-full ${
                  artist?.status === 'active' ? 'bg-success' : 
                  artist?.status === 'pending' ? 'bg-warning' : 'bg-gray-300'
                }`} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SearchResults;